import { useState, useCallback, useEffect, useMemo } from 'react';
import { ParsedGraph, ValidationError } from '../types/ontology';
import { parseRDF } from '../utils/rdfParser';
import { validateRDF } from '../components/Editor/validation/rdfValidator';
import { OntologyStats } from '../components/Footer/Footer';

export function useOntology(initialTurtle: string) {
    const [turtleText, setTurtleText] = useState(initialTurtle);
    const [graph, setGraph] = useState<ParsedGraph | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [isValidating, setIsValidating] = useState(false);

    // Debounced validation and parsing
    useEffect(() => {
        const timeoutId = setTimeout(async () => {
            setIsValidating(true);
            try {
                // First validate syntax
                const errors = await validateRDF(turtleText);
                setValidationErrors(errors);

                // If no errors, parse the graph
                if (errors.filter(e => e.severity === 'error').length === 0) {
                    const parsedGraph = await parseRDF(turtleText);
                    setGraph(parsedGraph);
                }
            } catch (error) {
                console.error('Validation error:', error);
                setValidationErrors([{
                    line: 1,
                    column: 1,
                    message: error instanceof Error ? error.message : 'Unknown error',
                    severity: 'error'
                }]);
            } finally {
                setIsValidating(false);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [turtleText]);

    // Calculate stats from graph
    const ontologyStats: OntologyStats = useMemo(() => {
        if (!graph) {
            return {
                classes: 0,
                objectProperties: 0,
                datatypeProperties: 0,
                individuals: 0,
                triples: 0
            };
        }
        return {
            classes: graph.classes.length,
            objectProperties: graph.objectProperties.length,
            datatypeProperties: graph.datatypeProperties.length,
            individuals: graph.individuals.length,
            triples: graph.triples.length
        };
    }, [graph]);

    const updateTurtle = useCallback((newValue: string) => {
        setTurtleText(newValue);
    }, []);

    // Update graph from canvas edits
    const updateFromCanvas = useCallback((updatedGraph: ParsedGraph) => {
        setGraph(updatedGraph);
        // TODO: Serialize back to Turtle
    }, []);

    return {
        turtleText,
        setTurtleText: updateTurtle,
        graph,
        validationErrors,
        isValidating,
        ontologyStats,
        updateFromCanvas
    };
}
